import { createSnapshotConsumer } from '@highlight/ipc'
import { useSnapshotConsumer } from '@highlight/ipc/react'
import type { FocusedWindow } from '@highlight/common'
import { EMPTY_FOCUSED_WINDOW_SNAPSHOT, FOCUSED_WINDOW_TOPIC, type FocusedWindowSnapshot } from '@shared/focused-window'
import * as Sentry from '@sentry/electron/renderer'

const focusedWindowConsumer = createSnapshotConsumer<FocusedWindowSnapshot>({
  topic: FOCUSED_WINDOW_TOPIC,
  initialSnapshot: EMPTY_FOCUSED_WINDOW_SNAPSHOT,
  onError: (error) => {
    Sentry.captureException(error)
  },
})

export function useFocusedWindowSnapshot(): FocusedWindowSnapshot {
  return useSnapshotConsumer(focusedWindowConsumer)
}

/** The focused window once `matches` accepts it, or null if none does within `timeoutMs`. Usable outside React. */
export function waitForFocusedWindow(
  matches: (focused: FocusedWindow) => boolean,
  timeoutMs: number,
): Promise<FocusedWindow | null> {
  return new Promise((resolve) => {
    let settled = false
    const finish = (focused: FocusedWindow | null) => {
      if (settled) return
      settled = true
      window.clearTimeout(timeoutId)
      unsubscribe()
      resolve(focused)
    }
    const check = () => {
      const focused = focusedWindowConsumer.getSnapshot().focusedWindow
      if (focused && matches(focused)) finish(focused)
    }
    const unsubscribe = focusedWindowConsumer.subscribe(check)
    const timeoutId = window.setTimeout(() => finish(null), timeoutMs)
    check()
  })
}
